<template>
  <div class="admin-container">
    <div class="page-header">
      <div>
        <h1>后台管理系统</h1>
        <p>管理员可在这里维护首页实时统计数据与充值记录。</p>
      </div>
    </div>

    <el-tabs v-model="activeTab" type="border-card">
      <el-tab-pane label="实时数据管理" name="dashboard-summary">
        <div class="tab-actions">
          <el-select
            v-model="selectedUserId"
            placeholder="请选择用户"
            filterable
            size="small"
            class="user-select"
            @change="handleUserChange"
          >
            <el-option
              v-for="user in dashboardUsers"
              :key="user.id"
              :label="formatUserLabel(user)"
              :value="user.id"
            ></el-option>
          </el-select>
          <el-button @click="fetchDashboardSummary" :loading="dashboardLoading">刷新</el-button>
        </div>

        <el-table
          :data="summaryRows"
          border
          style="width: 100%"
          v-loading="dashboardLoading"
        >
          <el-table-column type="index" label="#" width="60"></el-table-column>
          <el-table-column prop="label" label="数据项" min-width="180"></el-table-column>
          <el-table-column prop="description" label="说明" min-width="240"></el-table-column>
          <el-table-column label="当前值" width="220">
            <template slot-scope="scope">
              <el-input
                v-if="scope.row.editing"
                v-model="scope.row.editValue"
                type="number"
                size="mini"
                class="summary-input"
                :ref="`summaryInput-${scope.row.key}`"
                @keyup.enter.native="saveSummaryRow(scope.row)"
              ></el-input>
              <span v-else>{{ scope.row.value }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="unit" label="单位" width="100"></el-table-column>
          <el-table-column label="操作" width="200">
            <template slot-scope="scope">
              <el-button
                v-if="!scope.row.editing"
                size="mini"
                type="primary"
                @click="startSummaryEdit(scope.row)"
              >
                编辑
              </el-button>
              <el-button
                v-if="scope.row.editing"
                size="mini"
                type="success"
                :loading="dashboardSaving"
                @click="saveSummaryRow(scope.row)"
              >
                保存
              </el-button>
              <el-button
                v-if="scope.row.editing"
                size="mini"
                @click="cancelSummaryEdit(scope.row)"
              >
                取消
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane label="充值记录管理" name="recharge">
        <div class="tab-actions">
          <el-select
            v-model="selectedUserId"
            placeholder="请选择用户"
            filterable
            size="small"
            class="user-select"
            @change="handleUserChange"
          >
            <el-option
              v-for="user in dashboardUsers"
              :key="user.id"
              :label="formatUserLabel(user)"
              :value="user.id"
            ></el-option>
          </el-select>
          <el-button type="primary" @click="handleAdd">新增记录</el-button>
          <el-button type="success" @click="fetchRechargeHistory" :loading="loading">
            <i class="el-icon-refresh"></i> 刷新数据
          </el-button>
          <el-button type="warning" @click="exportToExcel">
            <i class="el-icon-download"></i> 导出Excel
          </el-button>
        </div>

        <el-table
          :data="tableData"
          border
          style="width: 100%"
          v-loading="loading"
          @cell-click="handleCellClick"
          :default-sort="{prop: 'date', order: 'descending'}"
        >
          <el-table-column prop="order_id" label="订单号" width="220" :show-overflow-tooltip="true">
            <template slot-scope="scope">
              {{ scope.row.order_id }}
            </template>
          </el-table-column>
          <el-table-column prop="date" label="日期" width="180" sortable>
            <template slot-scope="scope">
              {{ scope.row.date }}
            </template>
          </el-table-column>
          <el-table-column prop="payer" label="付款方" width="180"></el-table-column>
          <el-table-column prop="receiver" label="收款方" width="180"></el-table-column>

          <el-table-column prop="amount" label="金额（元）" width="180">
            <template slot-scope="scope">
              <div v-if="scope.row.editing">
                <el-input-number
                  v-model="scope.row.amount"
                  :min="0"
                  :precision="2"
                  @blur="handleEditConfirm(scope.row)"
                  @keyup.enter.native="handleEditConfirm(scope.row)"
                  controls-position="right"
                  size="mini"
                  ref="amountInput"
                ></el-input-number>
              </div>
              <div v-else @click="handleEditStart(scope.row)">
                {{ formatCurrency(scope.row.amount || 0) }}
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="points" label="点数" width="150">
            <template slot-scope="scope">
              <div v-if="scope.row.editing">
                <el-input-number
                  v-model="scope.row.points"
                  :min="0"
                  :precision="0"
                  @blur="handleEditConfirm(scope.row)"
                  @keyup.enter.native="handleEditConfirm(scope.row)"
                  controls-position="right"
                  size="mini"
                  ref="pointsInput"
                ></el-input-number>
              </div>
              <div v-else @click="handleEditStart(scope.row)">
                {{ formatNumber(scope.row.points || 0) }}
              </div>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="180">
            <template slot-scope="scope">
              <el-button size="mini" @click="handleRowEdit(scope.row)">编辑</el-button>
              <el-button size="mini" type="danger" @click="handleRowDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="50%">
      <el-form :model="editForm" :rules="formRules" ref="editForm" label-width="100px">
        <el-form-item label="订单号" v-if="currentEditMode === 'edit'">
          <el-input v-model="editForm.order_id" disabled></el-input>
        </el-form-item>
        <el-form-item label="日期">
          <el-date-picker
            v-model="editForm.date"
            type="date"
            placeholder="选择日期"
            value-format="yyyy-MM-dd"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="付款方">
          <el-input v-model="editForm.payer"></el-input>
        </el-form-item>
        <el-form-item label="收款方">
          <el-input v-model="editForm.receiver"></el-input>
        </el-form-item>
        <el-form-item label="金额">
          <el-input v-model="editForm.amount"></el-input>
        </el-form-item>
        <el-form-item label="点数">
          <el-input v-model="editForm.points"></el-input>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleDialogConfirm">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getRechargeHistory, createOrder, updateOrder, deleteOrder } from '@/api/pay'
import { getDashboardSummary, updateDashboardSummary, getDashboardUsers } from '@/api/statistics'
import * as XLSX from 'xlsx'

function getDefaultDashboardForm() {
  return {
    running_tasks: 0,
    customer_files: 0,
    total_analysis_tasks: 0,
    total_videos: 0,
    remaining_points: 0,
    consumed_points: 0,
    total_recharge_points: 0,
    current_deducted_points: 0
  }
}

export default {
  name: 'AdminPage',
  data() {
    return {
      activeTab: 'dashboard-summary',
      loading: false,
      dashboardLoading: false,
      dashboardSaving: false,
      dashboardUsers: [],
      selectedUserId: null,
      summaryRows: [],
      tableData: [],
      dialogVisible: false,
      editForm: {
        id: null,
        date: '',
        payer: '',
        receiver: '',
        amount: 0,
        points: 0
      },
      dashboardForm: getDefaultDashboardForm(),
      summaryFields: [
        { key: 'running_tasks', label: '当前运行任务', description: '首页实时统计卡片中的任务运行数', unit: '项' },
        { key: 'customer_files', label: '客户档案', description: '首页实时统计卡片中的客户档案数', unit: '人' },
        { key: 'total_analysis_tasks', label: '总分析任务', description: '首页实时统计卡片中的分析任务总数', unit: '项' },
        { key: 'total_videos', label: '总视频数', description: '首页实时统计卡片中的视频总数', unit: '个' },
        { key: 'remaining_points', label: '剩余点数', description: '首页实时统计卡片中的剩余点数', unit: '点' },
        { key: 'consumed_points', label: '消耗点数', description: '首页实时统计卡片中的已消耗点数', unit: '点' },
        { key: 'total_recharge_points', label: '总充值点数', description: '首页实时统计卡片中的总充值点数', unit: '点' },
        { key: 'current_deducted_points', label: '当前扣除点数', description: '首页实时统计卡片中的当前扣除点数', unit: '点' }
      ],
      currentEditMode: 'add',
      formRules: {
        payer: [
          { required: true, message: '请输入付款方', trigger: 'blur' }
        ],
        receiver: [
          { required: true, message: '请输入收款方', trigger: 'blur' }
        ],
        amount: [
          { required: true, message: '请输入金额', trigger: 'blur' },
          { type: 'number', min: 0, message: '金额必须大于0', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.currentEditMode === 'add' ? '新增记录' : '编辑记录'
    }
  },
  created() {
    this.fetchDashboardUsers()
  },
  methods: {
    formatUserLabel(user) {
      const company = user.company_name ? ` / ${user.company_name}` : ''
      const nickname = user.nickname ? ` (${user.nickname})` : ''
      return `${user.username}${nickname}${company}`
    },
    async fetchDashboardUsers() {
      this.dashboardLoading = true
      try {
        const { data } = await getDashboardUsers()
        this.dashboardUsers = Array.isArray(data) ? data : []
        if (!this.selectedUserId && this.dashboardUsers.length > 0) {
          this.selectedUserId = this.dashboardUsers[0].id
        }
        if (this.selectedUserId) {
          await Promise.all([
            this.fetchDashboardSummary(),
            this.fetchRechargeHistory()
          ])
        }
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.$message.error(error.response?.data?.detail || '获取用户列表失败')
      } finally {
        this.dashboardLoading = false
      }
    },
    async fetchDashboardSummary() {
      if (!this.selectedUserId) {
        this.summaryRows = []
        return
      }
      this.dashboardLoading = true
      try {
        const { data } = await getDashboardSummary({ target_user_id: this.selectedUserId })
        this.dashboardForm = {
          ...getDefaultDashboardForm(),
          ...data
        }
        this.summaryRows = this.summaryFields.map(field => ({
          ...field,
          value: this.dashboardForm[field.key],
          editing: false,
          originalValue: this.dashboardForm[field.key],
          editValue: String(this.dashboardForm[field.key])
        }))
      } catch (error) {
        console.error('获取首页实时数据失败:', error)
        this.$message.error(error.response?.data?.detail || '获取首页实时数据失败')
      } finally {
        this.dashboardLoading = false
      }
    },
    handleUserChange() {
      this.fetchDashboardSummary()
      this.fetchRechargeHistory()
    },
    startSummaryEdit(row) {
      this.summaryRows.forEach(item => {
        item.editing = false
      })
      row.originalValue = row.value
      row.editValue = String(row.value)
      row.editing = true
      this.$nextTick(() => {
        const inputRef = this.$refs[`summaryInput-${row.key}`]
        if (Array.isArray(inputRef) && inputRef[0]) {
          inputRef[0].focus()
        } else if (inputRef && typeof inputRef.focus === 'function') {
          inputRef.focus()
        }
      })
    },
    cancelSummaryEdit(row) {
      row.value = row.originalValue
      row.editValue = String(row.originalValue)
      row.editing = false
    },
    async saveSummaryRow(row) {
      const parsedValue = Number(row.editValue)
      if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        this.$message.warning('请输入大于等于 0 的数字')
        return
      }
      this.dashboardForm[row.key] = Math.floor(parsedValue)
      this.dashboardSaving = true
      try {
        await updateDashboardSummary({
          ...this.dashboardForm,
          target_user_id: this.selectedUserId
        })
        row.originalValue = this.dashboardForm[row.key]
        row.value = this.dashboardForm[row.key]
        row.editValue = String(this.dashboardForm[row.key])
        row.editing = false
        this.$message.success('实时数据已保存')
        await this.fetchDashboardSummary()
      } catch (error) {
        console.error('保存首页实时数据失败:', error)
        this.$message.error(error.response?.data?.detail || '保存首页实时数据失败')
      } finally {
        this.dashboardSaving = false
      }
    },
    async fetchRechargeHistory() {
      if (!this.selectedUserId) {
        this.tableData = []
        return
      }
      this.loading = true
      try {
        const response = await getRechargeHistory({ target_user_id: this.selectedUserId })
        if (response.status === 200) {
          this.tableData = response.data.map(item => ({
            ...item,
            id: item.order_id,
            amount: item.amount || 0,
            points: item.points || 0,
            editing: false
          }))
        } else {
          this.$message.error('获取数据失败')
        }
      } catch (error) {
        console.error('获取充值记录失败:', error)
        this.$message.error('获取数据异常')
      } finally {
        this.loading = false
      }
    },
    formatCurrency(value) {
      const num = Number(value || 0)
      if (isNaN(num)) return '¥0.00'
      return '¥' + num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    formatNumber(value) {
      const num = Number(value || 0)
      if (isNaN(num)) return '0'
      return num.toLocaleString()
    },
    handleEditStart(row) {
      this.tableData.forEach(item => {
        item.editing = false
      })
      row.editing = true
      this.$nextTick(() => {
        if (this.$refs.amountInput) {
          this.$refs.amountInput.focus()
        }
      })
    },
    async handleEditConfirm(row) {
      try {
        const updates = {
          amount: row.amount || 0,
          payer: row.payer,
          receiver: row.receiver,
          points: row.points || 0,
          target_user_id: this.selectedUserId
        }
        const response = await updateOrder(row.order_id, updates)
        if (response.error) {
          throw new Error(response.error)
        }
        row.editing = false
        this.$message.success('修改已保存')
        await this.fetchRechargeHistory()
      } catch (error) {
        console.error('更新失败:', error)
        this.$message.error(`更新失败: ${error.message || '未知错误'}`)
        await this.fetchRechargeHistory()
      }
    },
    handleCellClick(row, column) {
      if (column.label === '操作') return
    },
    handleAdd() {
      if (!this.selectedUserId) {
        this.$message.warning('请先选择用户')
        return
      }
      this.currentEditMode = 'add'
      this.editForm = {
        id: Date.now(),
        date: new Date().toISOString().split('T')[0],
        payer: '',
        receiver: '',
        amount: 0,
        points: 0
      }
      this.dialogVisible = true
    },
    handleRowEdit(row) {
      this.currentEditMode = 'edit'
      this.editForm = {
        order_id: row.order_id,
        date: row.date,
        payer: row.payer,
        receiver: row.receiver,
        amount: Number(row.amount),
        points: Number(row.points)
      }
      if (isNaN(this.editForm.amount)) this.editForm.amount = 0
      if (isNaN(this.editForm.points)) this.editForm.points = 0
      this.dialogVisible = true
    },
    async handleRowDelete(row) {
      try {
        await this.$confirm('确定要删除这条记录吗?', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        this.loading = true
        const response = await deleteOrder(row.order_id, { target_user_id: this.selectedUserId })
        if (response.error) {
          throw new Error(response.error)
        }
        this.$message.success('删除成功')
        await this.fetchRechargeHistory()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      } finally {
        this.loading = false
      }
    },
    async handleDialogConfirm() {
      try {
        const valid = await this.$refs.editForm.validate()
        if (!valid) return
        this.loading = true
        const orderData = {
          amount: Number(this.editForm.amount),
          payer: this.editForm.payer.trim(),
          receiver: this.editForm.receiver.trim(),
          points: Number(this.editForm.points) || 0
        }
        let response
        if (this.currentEditMode === 'add') {
          response = await createOrder({
            ...orderData,
            target_user_id: this.selectedUserId,
            payment_type: 0,
            status: 'unpaid'
          })
        } else {
          response = await updateOrder(this.editForm.order_id, {
            ...orderData,
            target_user_id: this.selectedUserId
          })
        }
        if (response.error) {
          throw new Error(response.error)
        }
        this.$message.success(response.message || (this.currentEditMode === 'add' ? '订单创建成功' : '订单更新成功'))
        this.dialogVisible = false
        await this.fetchRechargeHistory()
      } catch (error) {
        console.error('操作失败:', error)
        let errorMessage = this.currentEditMode === 'add' ? '订单创建失败' : '订单更新失败'
        if (error.response) {
          if (error.response.status === 404) {
            errorMessage = '接口不存在，请检查路径是否正确'
          } else if (error.response.data && error.response.data.error) {
            errorMessage = error.response.data.error
          }
        } else if (error.message) {
          errorMessage = error.message
        }
        this.$message.error(errorMessage)
      } finally {
        this.loading = false
      }
    },
    exportToExcel() {
      const exportData = this.tableData.map(item => ({
        日期: item.date,
        付款方: item.payer,
        收款方: item.receiver,
        金额: this.formatCurrency(item.amount),
        点数: this.formatNumber(item.points)
      }))
      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(exportData)
      XLSX.utils.book_append_sheet(wb, ws, '充值记录')
      const dateStr = new Date().toISOString().slice(0, 10)
      XLSX.writeFile(wb, `充值记录_${dateStr}.xlsx`)
      this.$message.success('导出成功')
    }
  }
}
</script>

<style scoped>
.admin-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

h1 {
  margin-top: 0;
  margin-bottom: 10px;
}

p {
  color: #666;
  margin-bottom: 0;
}

.tab-actions {
  margin-bottom: 20px;
}

.tab-actions .el-button {
  margin-right: 10px;
}

.user-select {
  width: 320px;
  margin-right: 10px;
}

.summary-input {
  width: 100%;
}

.el-table {
  margin-top: 20px;
}

.el-input-number {
  width: 100%;
}
</style>
